// ═══════════════════════════════════════════════════════════════════════════════
// Employer — Task history with date range and status filters
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { scrollMainAppAnchorIntoView } from '../../lib/mainAppScroll';
import { EmployerTaskSubPageHeader } from '../../components/task/employer/EmployerTaskSubPageHeader';
import { EmployerHistoryTaskRow, submittedAtSortKey } from '../../components/task/employer/EmployerHistoryTaskRow';
import { StatusFilterChipRow } from '../../components/ui/StatusFilterChipRow';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { taskInstanceApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { buildEmployerGroupedTaskDateHeading } from '../../utils/employerTaskDateHeading';
import { addCalendarDaysToYmd, getDateInTimezone, isIsoYmd } from '../../utils/employerTaskDayGrouping';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import type { TodayTodoItem } from '@fdh/shared';

type HistoryStatusTab = 'all' | 'not_submitted' | 'pending' | 'approved' | 'needs_update' | 'cancel';

const isHistoryStatusTab = (value: string | null): value is HistoryStatusTab =>
  value === 'all' ||
  value === 'not_submitted' ||
  value === 'pending' ||
  value === 'approved' ||
  value === 'needs_update' ||
  value === 'cancel';

/** Scroll the main app shell to the history list anchor after pending-review navigation. */
const scrollEmployerHistoryListAnchorIntoView = (): void => {
  const el = document.getElementById('employer-history-task-list-anchor');
  if (!el) {
    return;
  }
  el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'auto' });

  scrollMainAppAnchorIntoView(el, 12);
};

export const EmployerHistoryTasksPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const timezone = resolveDisplayTimeZone(user?.settings.timezone);
  const todayYmd = useMemo(() => getDateInTimezone(timezone), [timezone]);
  const defaultHistoryStart = useMemo(() => addCalendarDaysToYmd(todayYmd, -6), [todayYmd]);

  const histStartParam = searchParams.get('histStart');
  const histEndParam = searchParams.get('histEnd');
  const histStatusParam = searchParams.get('histStatus');

  const historyStartDate = isIsoYmd(histStartParam) ? histStartParam : defaultHistoryStart;
  const historyEndDate = isIsoYmd(histEndParam) ? histEndParam : todayYmd;
  const historyStatusTab: HistoryStatusTab = isHistoryStatusTab(histStatusParam)
    ? histStatusParam
    : 'all';

  const [historyTasks, setHistoryTasks] = useState<TodayTodoItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!isIsoYmd(next.get('histStart'))) {
      next.set('histStart', defaultHistoryStart);
      changed = true;
    }
    if (!isIsoYmd(next.get('histEnd'))) {
      next.set('histEnd', todayYmd);
      changed = true;
    }
    if (!isHistoryStatusTab(next.get('histStatus'))) {
      next.set('histStatus', 'all');
      changed = true;
    }
    const hs = next.get('histStart');
    const he = next.get('histEnd');
    if (isIsoYmd(hs) && hs > todayYmd) {
      next.set('histStart', todayYmd);
      changed = true;
    }
    if (isIsoYmd(he) && he > todayYmd) {
      next.set('histEnd', todayYmd);
      changed = true;
    }
    const hs2 = next.get('histStart');
    const he2 = next.get('histEnd');
    if (isIsoYmd(hs2) && isIsoYmd(he2) && hs2 > he2) {
      next.set('histEnd', hs2);
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [defaultHistoryStart, searchParams, setSearchParams, todayYmd]);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const response = await taskInstanceApi.history(historyStartDate, historyEndDate, historyStatusTab);
      if (!response.success || !response.data) {
        setHistoryError(response.error ?? t('task.list.loadError'));
        return;
      }
      setHistoryTasks(response.data.tasks ?? []);
    } catch {
      setHistoryError(t('task.list.loadError'));
    } finally {
      setHistoryLoading(false);
    }
  }, [historyEndDate, historyStartDate, historyStatusTab, t]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  useLayoutEffect(() => {
    setHistoryLoading(true);
  }, [historyEndDate, historyStartDate, historyStatusTab]);

  const histScrollParam = searchParams.get('histScroll');

  useEffect(() => {
    if (histScrollParam !== '1') {
      return;
    }
    if (historyError) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('histScroll');
          return next;
        },
        { replace: true },
      );
      return;
    }
    if (historyLoading) {
      return;
    }

    const clearHistScroll = (): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('histScroll');
          return next;
        },
        { replace: true },
      );
    };

    let cancelled = false;
    const run = (): void => {
      if (cancelled) {
        return;
      }
      scrollEmployerHistoryListAnchorIntoView();
    };

    run();
    const rafOuter = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(run);
    });
    const tMid = window.setTimeout(run, 96);
    const tDone = window.setTimeout(() => {
      if (cancelled) {
        return;
      }
      run();
      clearHistScroll();
    }, 320);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(rafOuter);
      window.clearTimeout(tMid);
      window.clearTimeout(tDone);
    };
  }, [histScrollParam, historyError, historyLoading, historyTasks.length, setSearchParams]);

  const historyTasksGrouped = useMemo(() => {
    const byDate = new Map<string, TodayTodoItem[]>();
    historyTasks.forEach((task) => {
      const d = task.dueDate;
      const list = byDate.get(d) ?? [];
      list.push(task);
      byDate.set(d, list);
    });
    const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
    return dates.map((date) => ({
      date,
      tasks: (byDate.get(date) ?? [])
        .slice()
        .sort((a, b) => {
          const cmp = submittedAtSortKey(b.submittedAt).localeCompare(submittedAtSortKey(a.submittedAt));
          if (cmp !== 0) return cmp;
          return a.title.localeCompare(b.title);
        }),
    }));
  }, [historyTasks]);

  const historyStatusTabs: Array<{ key: HistoryStatusTab; label: string }> = [
    { key: 'all', label: t('task.history.statusAll') },
    { key: 'not_submitted', label: t('task.status.notSubmitted') },
    { key: 'pending', label: t('task.status.pending') },
    { key: 'approved', label: t('task.status.approved') },
    { key: 'needs_update', label: t('task.status.needsUpdate') },
    { key: 'cancel', label: t('task.status.cancel') },
  ];

  const historyStatusFilterOptions = useMemo(
    () => historyStatusTabs.map((tab) => ({ value: tab.key, label: tab.label })),
    [historyStatusTabs],
  );

  const handleBack = () => {
    navigateBack(navigate, '/tasks');
  };

  const handleHistoryStartDateChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('histStart', value);
    const endRaw = next.get('histEnd');
    if (isIsoYmd(endRaw) && value > endRaw) {
      next.set('histEnd', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleHistoryEndDateChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('histEnd', value);
    const startRaw = next.get('histStart');
    if (isIsoYmd(startRaw) && value < startRaw) {
      next.set('histStart', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleHistoryStatusTab = (nextStatus: HistoryStatusTab): void => {
    const next = new URLSearchParams(searchParams);
    next.set('histStatus', nextStatus);
    setSearchParams(next, { replace: true });
  };

  const handleTaskInstanceDetailClick = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}`);
  };

  return (
    <div className="min-h-full bg-[#f7f8fb] dark:bg-gray-950">
      <EmployerTaskSubPageHeader title={t('task.list.menuHistoryTasks')} onBack={handleBack} />

      <div className="mx-auto max-w-lg space-y-3 px-4 pt-4">
        <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-gray-900">
          <div className="flex flex-wrap items-center gap-2">
            <IsoDatePicker
              id="employer-task-history-start-date"
              ariaLabel={t('task.history.startDate')}
              value={historyStartDate}
              onChange={handleHistoryStartDateChange}
              maxDate={historyEndDate || todayYmd}
              className="min-w-0 flex-1 basis-[140px]"
              valueFormat="iso"
            />
            <span
              className="shrink-0 px-0.5 text-sm font-medium text-gray-500 dark:text-gray-400"
              aria-hidden
            >
              {t('settlement.helperConfirmDateRangeSeparator')}
            </span>
            <IsoDatePicker
              id="employer-task-history-end-date"
              ariaLabel={t('task.history.endDate')}
              value={historyEndDate}
              onChange={handleHistoryEndDateChange}
              minDate={historyStartDate || undefined}
              maxDate={todayYmd}
              className="min-w-0 flex-1 basis-[140px]"
              valueFormat="iso"
            />
          </div>
        </div>
        <StatusFilterChipRow
          ariaLabel={t('task.history.filterAria')}
          options={historyStatusFilterOptions}
          value={historyStatusTab}
          onChange={handleHistoryStatusTab}
        />
        <div
          id="employer-history-task-list-anchor"
          className="pointer-events-none h-px w-full max-w-full scroll-mt-28 overflow-hidden"
          aria-hidden
        />
      </div>

      <main className={`mx-auto max-w-lg px-4 py-4 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
        {historyLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : historyError ? (
          <div className="rounded-xl bg-red-50 p-6 text-center dark:bg-red-950/40">
            <p className="text-red-600 dark:text-red-300" role="alert">
              {historyError}
            </p>
            <button
              type="button"
              onClick={() => void fetchHistory()}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none dark:text-red-200"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : historyTasks.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm dark:bg-gray-900">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/45">
              <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-gray-600 dark:text-gray-300">{t('task.history.empty')}</p>
            <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">{t('task.history.emptyHint')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            {historyTasksGrouped.map((group) => (
              <section
                key={group.date}
                className="space-y-2"
                aria-labelledby={`employer-task-history-date-${group.date}`}
              >
                <h2
                  id={`employer-task-history-date-${group.date}`}
                  className="flex items-baseline justify-between gap-2 text-sm font-semibold tracking-tight text-gray-600 dark:text-gray-300"
                >
                  <span className="min-w-0">
                    {buildEmployerGroupedTaskDateHeading({
                      dateYmd: group.date,
                      todayYmd,
                      tomorrowYmd: null,
                      timezone,
                      language,
                      t,
                    })}
                  </span>
                  <span className="shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">
                    {t('task.history.groupTotalCount', { count: group.tasks.length })}
                  </span>
                </h2>
                <ul className="space-y-2" role="list">
                  {group.tasks.map((task) => (
                    <EmployerHistoryTaskRow
                      key={task.taskInstanceId}
                      task={task}
                      onViewDetail={handleTaskInstanceDetailClick}
                      t={t}
                    />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};
